// Dispatch plan: the day the dispatcher will run, as one table — a row per
// order, grouped under each trip, each trip under its truck — built from one
// saved run and published to drivers. The server owns the plan: it is
// versioned, validated and published there. This page keeps a working copy the
// dispatcher edits (stop order, which trip an order rides, orders taken off the
// plan, trips and trucks added, each truck's vehicle and driver, addresses,
// delivery windows, date) and sends it back on "Recalibrate", which saves and
// re-checks it in one step. Orders the run didn't plan are added through the order
// selector (AddOrdersDialog), saved the same way. Edit rules and the lifecycle
// live in lib/planEditor. A plan with 3PL trucks books their carriers below
// the table (CarrierBookings).

import {
  type CSSProperties,
  type DragEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  type AddedOrderPayload,
  ApiError,
  closePlan,
  createPlan,
  downloadPlanWorkbook,
  getPlan,
  getPlanStatuses,
  publishPlan,
  refreshDelivery,
  resetPlan,
  validatePlan,
  withdrawPlan,
} from "../api/client";
import { clockAt, clockOnDay, formatClock, todayIn } from "../lib/format";
import { type MsgKey, useLang, useT } from "../lib/i18n";
import { STATUS_TONE } from "../lib/orderStatus";
import {
  type PlanEdit,
  type PlanPhase,
  type RequestState,
  type WorkingPlan,
  applyEdit,
  canPublish,
  changeCount,
  freeVehicles,
  isDirty,
  issuesHoldingBack,
  nextTripId,
  nextTruckId,
  planPhase,
  removableTrip,
  removableTruck,
  type RouteStop,
  routeStops,
  stepEdit,
  truckColor,
  tripOf,
  unsentRouteIds,
  workingFrom,
} from "../lib/planEditor";
import type {
  DeliveryDrop,
  DispatchPlan,
  IssueSeverity,
  OrderStatus,
  PlanDocument,
  PlanIssue,
  PlanOrder,
  PlanRoute,
  PlanTruck,
  PlanWindow,
  LoadingVerdict,
  PlanShipment,
  PlanSource,
  RouteFeatureCollectionDTO,
  RoutePublication,
} from "../types";
import { AddOrdersDialog } from "./AddOrdersDialog";
import { CarrierBookings } from "./CarrierBookings";
import { Dialog } from "./Dialog";
import { RouteMap } from "./RouteMap";
import { DEFAULT_DEPARTURE_MIN, DEPARTURE_SLOTS } from "./TruckDepartures";

type T = ReturnType<typeof useT>;
type Selection = { routeId: string; zip?: string } | null;
type ActionError = { kind: "conflict" | "rejected" | "failed"; message: string; errors?: string[] };

const SEVERITIES: IssueSeverity[] = ["blocking", "warning", "info"];
// The server's limit on an order's notes (MAX_NOTES_CHARS in plan_validation).
const MAX_NOTES_CHARS = 1000;

// The table's columns, in order; a route's header row spans them all.
const COLUMNS = [
  "stop",
  "order",
  "customer",
  "zip",
  "pallets",
  "weight",
  "window",
  "eta",
  "address",
  "notes",
  "status",
  "actions",
] as const;

const PHASE_TONE: Record<PlanPhase, string> = {
  draft: "neutral",
  modified: "caution",
  validating: "accent",
  valid: "ok",
  invalid: "warn",
  validation_error: "warn",
  publishing: "accent",
  published: "ok",
  partially_published: "caution",
  publish_failed: "warn",
  closed: "neutral",
};

// Issue codes the page can phrase itself (in either language); anything else
// falls back to the server's English message.
const ISSUE_CODES = [
  "vehicle_missing",
  "vehicle_unknown",
  "driver_missing",
  "driver_unknown",
  "driver_unavailable",
  "driver_multiple_vehicles",
  "vehicle_multiple_trucks",
  "no_53ft_access",
  "pallet_does_not_fit",
  "plan_date_past",
  "another_plan_published",
  "address_missing",
  "address_outside_area",
  "customer_pickup",
  "route_empty",
  "excluded_shipments",
  "overweight",
  "over_floor_area",
  "late",
  "early",
  "driver_hours",
  "departure_passed",
  "loading_infeasible",
  "loading_not_verified",
  "loading_order_unverified",
  "orders_unrouted",
  "order_on_another_plan",
  "carrier_unassigned",
] as const;
type IssueCode = (typeof ISSUE_CODES)[number];

// What a route's header says about the last recalibration's loading check.
const LOADING_NOTE: Record<LoadingVerdict, MsgKey | null> = {
  verified: "plan.loading.verified",
  order_unverified: "plan.loading.order_unverified",
  not_verified: "plan.loading.not_verified",
  infeasible: "plan.loading.infeasible",
  sequenced: "plan.loading.sequenced",
  skipped_precheck: null, // the capacity warning already says why
};

// A trip by its truck and its place among the truck's trips; a 3PL lane by its
// number. A route the plan no longer has (sent once, then reset away) by its id.
function routeLabel(doc: PlanDocument, routeId: string, t: T): string {
  const trip = tripOf(doc, routeId);
  if (trip) return `${trip.truckId} · ${t("plan.trip", { n: trip.n })}`;
  const lane = /^3PL-(\d+)$/.exec(routeId);
  if (lane) return t("plan.threeplLane", { n: lane[1] });
  const added = /^R(\d+)$/.exec(routeId);
  if (added) return t("plan.addedRoute", { n: added[1] });
  const m = /^(.+)-trip(\d+)$/.exec(routeId);
  return m ? `${m[1]} · ${t("plan.trip", { n: Number(m[2]) + 1 })}` : routeId;
}

// A truck's colour on the page, as on the map; a 3PL lane has the 3PL colour.
function routeColor(route: PlanRoute): string {
  return route.carrier === "3pl" || !route.truck_id ? THREEPL_COLOR : truckColor(route.truck_id);
}

// The results map's 3PL colour, so a lane reads the same on both pages.
const THREEPL_COLOR = "#9C27B0";

const stopDomId = (routeId: string, zip: string) => `plan-stop-${routeId}-${zip}`;
const orderDomId = (routeId: string, orderNo: string) => `plan-order-${routeId}-${orderNo}`;
const routeDomId = (routeId: string) => `plan-route-${routeId}`;
const truckDomId = (truckId: string) => `plan-truck-${truckId}`;
const stepDomId = (routeId: string, orderNo: string, delta: -1 | 1) =>
  `plan-step-${delta < 0 ? "up" : "down"}-${routeId}-${orderNo}`;

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function issueText(issue: PlanIssue, t: T, doc: PlanDocument): string {
  if (!(ISSUE_CODES as readonly string[]).includes(issue.code)) return issue.message;
  const vars: Record<string, string | number> = {
    route: issue.route_id ? routeLabel(doc, issue.route_id, t) : "",
    zip: issue.zip ?? "",
  };
  for (const [k, v] of Object.entries(issue.params)) {
    if (v === null) continue;
    if (k === "eta_min") vars.eta = formatClock(Number(v));
    else if (k === "deadline_min") vars.deadline = formatClock(Number(v));
    else if (k === "after_min") vars.after = formatClock(Number(v));
    else if (k === "depart_min") vars.depart = formatClock(Number(v));
    else if (k === "weight_over_kg") vars[k] = Math.round(Number(v)).toLocaleString();
    else if (k === "footprint_pct") vars[k] = Math.round(Number(v));
    else vars[k] = v;
  }
  return t(`plan.issue.${issue.code as IssueCode}`, vars);
}

export function DispatchPlanView({
  source,
  onBack,
  onDirtyChange,
  onSavedPlans,
  onTrackDeliveries,
  onOpenPlan,
  focusTruckId,
}: {
  source: PlanSource;
  onBack: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  // Shows the dispatcher's saved plans (the list the page can be opened from).
  onSavedPlans?: () => void;
  // Shows the Deliveries tab on the plan's depot and day.
  onTrackDeliveries?: (depotZip: string, planDate: string) => void;
  // Opens another of the dispatcher's plans (one a warning names).
  onOpenPlan?: (planId: string) => void;
  // The truck to land on once the plan loads (opened from Deliveries).
  focusTruckId?: string;
}) {
  const t = useT();
  const { lang } = useLang();
  const [server, setServer] = useState<DispatchPlan | null>(null);
  const [working, setWorking] = useState<WorkingPlan | null>(null);
  const [request, setRequest] = useState<RequestState>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // The order selector, and the route it was opened from (null: the toolbar).
  const [adding, setAdding] = useState<{ routeId: string | null } | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The address being typed for an order (its row key), until Enter or Escape.
  const [addressDraft, setAddressDraft] = useState<{ key: string; text: string } | null>(null);
  // The notes being written for an order (its row key), until saved or Escape.
  const [notesDraft, setNotesDraft] = useState<{
    key: string;
    text: string;
    tooLong?: boolean;
  } | null>(null);
  // The window being set for an order (its row key), as "HH:MM" ("" for none).
  const [windowDraft, setWindowDraft] = useState<{
    key: string;
    after: string;
    deadline: string;
    error: boolean;
  } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // Trucks with a 3PL booking on record: a new truck doesn't take their number.
  const [bookedTrucks, setBookedTrucks] = useState<string[]>([]);
  const [loadKey, setLoadKey] = useState(0);
  // Each routed order's status ("route:order"), by the rule every tab uses.
  const [statuses, setStatuses] = useState<Record<string, OrderStatus>>({});
  // The unrouted orders ticked for "Put on trip".
  const [pooled, setPooled] = useState<Set<string>>(new Set());
  // Synchronous guard: a second click lands before React re-renders the
  // disabled button, so state alone can't stop a double submit.
  const busy = useRef(false);
  // The order row being dragged.
  const drag = useRef<{ routeId: string; index: number } | null>(null);
  const pendingFocus = useRef<string | null>(null);

  const adopt = useCallback((plan: DispatchPlan) => {
    setServer(plan);
    setWorking(workingFrom(plan));
  }, []);

  useEffect(() => {
    let live = true;
    setRequest("loading");
    setLoadError(null);
    ("plan_id" in source ? getPlan(source.plan_id) : createPlan(source))
      .then((plan) => {
        if (!live) return;
        adopt(plan);
        if (focusTruckId) pendingFocus.current = truckDomId(focusTruckId);
      })
      .catch((e) => live && setLoadError(errText(e)))
      .finally(() => live && setRequest("idle"));
    return () => {
      live = false;
    };
  }, [source, loadKey, adopt, focusTruckId]);

  // Read again whenever the plan or its delivery read changes; a plan page
  // without them still works, showing no statuses.
  const statusKey = server && `${server.id}:${server.version}:${server.delivery?.refreshed_at}`;
  useEffect(() => {
    if (!server) return;
    let live = true;
    getPlanStatuses(server.id)
      .then((rows) => {
        if (live) {
          setStatuses(Object.fromEntries(rows.map((r) => [`${r.route_id}:${r.order_no}`, r.status])));
        }
      })
      .catch(() => live && setStatuses({}));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey]);

  const dirty = server !== null && working !== null && isDirty(working, server);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Keep keyboard focus on the thing that just moved / was asked for. If it
  // is gone (the Publish button once published), land on the page heading
  // rather than dropping focus to <body>.
  useEffect(() => {
    if (pendingFocus.current) {
      const wanted = document.getElementById(pendingFocus.current);
      // A disabled button can't take focus (Publish once there's nothing to send).
      const target =
        wanted && !(wanted as HTMLButtonElement).disabled
          ? wanted
          : document.getElementById("plan-title");
      target?.focus();
      pendingFocus.current = null;
    }
  });

  const shipments = useMemo(
    () => new Map((server?.shipments ?? []).map((s) => [s.shipment_id, s])),
    [server],
  );
  // The last recalibration's routes, in the shape the results map draws.
  // Memoised so the map only rebuilds when the server answers.
  const mapData = useMemo(() => {
    const v = server?.validation;
    if (!v?.depot || v.depot.lat === null || v.depot.lon === null) return null;
    const routes: RouteFeatureCollectionDTO = {
      type: "FeatureCollection",
      features: Object.entries(v.routes).map(([rid, timing]) => {
        const route = server!.document.routes.find((r) => r.route_id === rid);
        const lane = route?.carrier === "3pl";
        const trip = tripOf(server!.document, rid);
        return {
          type: "Feature" as const,
          geometry: timing.geometry,
          properties: {
            kind: lane ? ("threepl" as const) : ("own_truck" as const),
            truck_label: trip?.truckId ?? rid,
            trip_index: trip ? trip.n - 1 : 0,
            route_id: rid,
            color: route ? routeColor(route) : THREEPL_COLOR,
            miles: timing.miles,
            n_stops: timing.stops.length,
            cost_usd: timing.cost_usd ?? 0,
            stops: timing.stops.map((s, i) => ({
              zip: s.zip,
              seq: i + 1,
              arrival_min: null,
              eta_clock: formatClock(s.eta_min),
              late_min: 0,
            })),
          },
        };
      }),
    };
    return {
      depot: { ...v.depot, lat: v.depot.lat, lon: v.depot.lon },
      routes,
      unrouted: v.unrouted ?? [],
    };
  }, [server]);

  if (!server || !working) {
    return (
      <main className="results plan-page">
        <div className="results-toolbar">
          <button className="btn btn-ghost" onClick={onBack}>
            {t("plan.back")}
          </button>
        </div>
        {loadError ? (
          <div role="alert" className="section-block">
            <p className="upload-status error">
              {t("plan.loadFailed")} {loadError}
            </p>
            <button className="btn btn-secondary" onClick={() => setLoadKey((k) => k + 1)}>
              {t("plan.retry")}
            </button>
          </div>
        ) : (
          <div className="plan-loading" role="status">
            <div className="spinner" />
            <p>{t("plan.loading")}</p>
          </div>
        )}
      </main>
    );
  }

  const plan = server;
  const editable = plan.can_edit && request === "idle";
  const phase = planPhase(plan, dirty, request);
  const publishable = canPublish(plan, dirty, request);
  const validation = plan.validation;
  const issues = validation?.issues ?? [];
  const timings = validation?.routes ?? {};
  const workingDoc = working.document;
  const labelOf = (routeId: string) => routeLabel(workingDoc, routeId, t);
  const routes = workingDoc.routes;
  const unrouted = workingDoc.unrouted;
  const activeRoutes = routes.filter((r) => r.orders.length > 0);
  const retrying = plan.status === "partially_published" || plan.status === "publish_failed";
  const toSend = unsentRouteIds(plan);
  const sentCount = Object.values(plan.publication?.routes ?? {}).filter(
    (r) => r.status === "sent",
  ).length;
  // What the publish button does: a first publish, a retry of routes that
  // failed to go out, or sending the edits made since drivers got the plan.
  const publishMode: PublishMode =
    sentCount === 0
      ? plan.status === "publish_failed"
        ? "retry"
        : "publish"
      : retrying && plan.changes.update.length + plan.changes.withdraw.length === 0
        ? "retry"
        : "changes";
  const changesPending = sentCount > 0 && !dirty && changeCount(plan) > 0;
  // From the working copy, so switching a lane back to the fleet clears it
  // before saving.
  const nothingToPublish =
    sentCount === 0 && unsentRouteIds({ ...plan, document: working.document }).length === 0;
  // A route its driver has started can't change: their app has it as it is.
  const started = new Set(plan.started_route_ids);
  const routeEditable = (routeId: string) => editable && !started.has(routeId);
  // Where an order can be moved, put back or added.
  const openRoutes = routes.filter((r) => !started.has(r.route_id));

  const vehicleName = (id: string | null) =>
    plan.roster.vehicles.find((v) => v.id === id)?.name ?? id ?? t("plan.unassigned");
  const driverName = (id: string | null) =>
    plan.roster.drivers.find((d) => d.id === id)?.name ?? id ?? t("plan.unassigned");

  function edit(change: PlanEdit, message: string, focusId?: string) {
    edits([change], message, focusId);
  }

  function moveStop(route: PlanRoute, from: number, to: number) {
    const stop = routeStops(route)[from];
    edit(
      { kind: "moveStop", routeId: route.route_id, from, to },
      t("plan.announceMoved", { zip: stop.zip, pos: to + 1, route: labelOf(route.route_id) }),
      stopDomId(route.route_id, stop.zip),
    );
  }

  function moveOrder(route: PlanRoute, index: number, toRouteId: string, toIndex?: number) {
    const order = route.orders[index];
    edit(
      { kind: "moveOrder", fromRouteId: route.route_id, index, toRouteId, toIndex },
      t("plan.announceMovedOrder", { order: order.order_no, route: labelOf(toRouteId) }),
      orderDomId(toRouteId, order.order_no),
    );
    setSelection({ routeId: toRouteId, zip: order.zip });
  }

  function reorderInStop(route: PlanRoute, from: number, to: number) {
    const order = route.orders[from];
    edit(
      { kind: "reorderInStop", routeId: route.route_id, from, to },
      t("plan.announceUnload", { order: order.order_no }),
      orderDomId(route.route_id, order.order_no),
    );
  }

  function takeOff(route: PlanRoute, index: number) {
    edit(
      { kind: "takeOff", routeId: route.route_id, index },
      t("plan.announceTakenOff", { order: route.orders[index].order_no }),
      "plan-unrouted",
    );
  }

  function putBack(index: number, toRouteId: string) {
    const order = unrouted[index];
    edit(
      { kind: "putBack", index, toRouteId },
      t("plan.announcePutBack", { order: order.order_no, route: labelOf(toRouteId) }),
      orderDomId(toRouteId, order.order_no),
    );
  }

  function putBackMany(orderNos: string[], toRouteId: string) {
    edit(
      { kind: "putBackMany", orderNos, toRouteId },
      t("plan.announcePutOnTrip", { n: orderNos.length, route: labelOf(toRouteId) }),
      orderDomId(toRouteId, orderNos[0]),
    );
    setPooled(new Set());
  }

  function edits(changes: PlanEdit[], message: string, focusId?: string) {
    if (!working || !editable) return;
    const document = changes.reduce(applyEdit, working.document);
    setWorking({ ...working, document });
    setAnnouncement(message);
    if (focusId) pendingFocus.current = focusId;
  }

  function addTrip(truckId: string) {
    const routeId = nextTripId(plan, workingDoc, truckId);
    const next = applyEdit(workingDoc, { kind: "addTrip", truckId, routeId });
    edit(
      { kind: "addTrip", truckId, routeId },
      t("plan.announceTripAdded", { route: routeLabel(next, routeId, t) }),
      routeDomId(routeId),
    );
  }

  function addTruck() {
    const truckId = nextTruckId(workingDoc, bookedTrucks);
    const routeId = nextTripId(plan, workingDoc, truckId);
    edit(
      { kind: "addTruck", truckId, routeId },
      t("plan.announceTruckAdded", { truck: truckId }),
      truckDomId(truckId),
    );
  }

  // A truck's vehicle, with the vehicle's usual driver when they're free today;
  // "3pl" hands all its trips to the 3PL carrier instead.
  function setTruckVehicle(truck: PlanTruck, value: string | null) {
    const id = truck.truck_id;
    if (value === "3pl") {
      edit(
        { kind: "setCarrier", truckId: id, carrier: "3pl" },
        t("plan.announceTruck3pl", { truck: id }),
      );
      return;
    }
    const vehicleId = value;
    const vehicle = plan.roster.vehicles.find((v) => v.id === vehicleId);
    const driver = plan.roster.drivers.find(
      (d) => d.id === vehicle?.default_driver_id && d.available,
    );
    edits(
      [
        ...(truck.carrier === "3pl"
          ? [{ kind: "setCarrier" as const, truckId: id, carrier: "fleet" as const }]
          : []),
        { kind: "setVehicle", truckId: truck.truck_id, vehicleId },
        ...(driver
          ? [{ kind: "setDriver" as const, truckId: truck.truck_id, driverId: driver.id }]
          : []),
      ],
      t("plan.announceVehicle", { route: truck.truck_id, v: vehicleName(vehicleId) }),
    );
  }

  // One step earlier or later: within the order's stop, else its whole stop
  // (see stepEdit). Focus stays on the arrow pressed, so it can be pressed
  // again, or moves to the other arrow once the order is at that end.
  function step(route: PlanRoute, index: number, delta: -1 | 1) {
    const change = stepEdit(route, index, delta);
    const order = route.orders[index];
    if (!change) return;
    const moved = applyEdit(workingDoc, change).routes.find((r) => r.route_id === route.route_id)!;
    const at = moved.orders.findIndex((o) => o.order_no === order.order_no);
    const focus = stepEdit(moved, at, delta) ? delta : (-delta as -1 | 1);
    edit(
      change,
      t(delta < 0 ? "plan.announceEarlier" : "plan.announceLater", { order: order.order_no }),
      stepDomId(route.route_id, order.order_no, focus),
    );
  }

  function endDrag() {
    drag.current = null;
    setDropTarget(null);
  }

  // Dropped on an order's row: an order from another route lands at that
  // order's stop; one from the same stop unloads there; one from another
  // stop of the same route takes its whole stop there. Dropped on an empty
  // route (`index` undefined), the order goes last.
  function dropOnRow(e: DragEvent, route: PlanRoute, index?: number) {
    e.preventDefault();
    const from = drag.current;
    endDrag();
    if (!from || !routeEditable(route.route_id) || !routeEditable(from.routeId)) return;
    const source = routes.find((r) => r.route_id === from.routeId);
    if (!source) return;
    const stops = routeStops(route);
    const target = index === undefined ? undefined : route.orders[index];
    if (from.routeId !== route.route_id) {
      const at = stops.find((s) => s.zip === target?.zip)?.orders[0].index;
      moveOrder(source, from.index, route.route_id, at);
    } else if (target && target.zip === route.orders[from.index].zip) {
      if (from.index !== index) reorderInStop(route, from.index, index!);
    } else if (target) {
      const fromStop = stops.findIndex((s) => s.zip === route.orders[from.index].zip);
      moveStop(route, fromStop, stops.findIndex((s) => s.zip === target.zip));
    }
  }

  function dragOver(e: DragEvent, key: string) {
    if (!drag.current) return;
    e.preventDefault();
    if (dropTarget !== key) setDropTarget(key);
  }

  async function revalidate() {
    if (busy.current || !working) return;
    busy.current = true;
    setRequest("validating");
    setActionError(null);
    try {
      const next = await validatePlan(plan.id, plan.version, working.plan_date, working.document);
      adopt(next);
      const counts = next.validation?.counts;
      setAnnouncement(
        t("plan.announceValidated", { b: counts?.blocking ?? 0, w: counts?.warning ?? 0 }),
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setActionError({ kind: "conflict", message: e.message });
      } else if (e instanceof ApiError && e.status === 422) {
        const errors = (e.detail as { errors?: string[] } | null)?.errors ?? [];
        setActionError({ kind: "rejected", message: e.message, errors });
      } else {
        setActionError({ kind: "failed", message: errText(e) });
      }
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  // Save the edits with the orders picked in the selector: one recalibration.
  // A refusal (an order that can't be added, a stale version) stays in the
  // selector with the picks, so the dispatcher can fix it there.
  async function addOrders(added: AddedOrderPayload[]) {
    if (busy.current || !working) return;
    busy.current = true;
    setRequest("validating");
    setAddError(null);
    try {
      adopt(await validatePlan(plan.id, plan.version, working.plan_date, working.document, added));
      setAdding(null);
      setAnnouncement(t("plan.announceAdded", { n: added.length }));
      const first = added[0];
      pendingFocus.current = first.route_id
        ? orderDomId(first.route_id, first.order_no)
        : "plan-unrouted";
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Changed elsewhere: the page's reload is the way on.
        setAdding(null);
        setActionError({ kind: "conflict", message: e.message });
      } else {
        const errors = (e instanceof ApiError && e.status === 422
          ? ((e.detail as { errors?: string[] } | null)?.errors ?? [])
          : []
        ).join(" ");
        setAddError(errors ? `${errText(e)} ${errors}` : errText(e));
      }
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  async function reset() {
    const addedOrders = new Set(plan.shipments.filter((s) => s.added).map((s) => s.order_no));
    const addedRoutes = routes.filter((r) => !plan.baseline_route_ids.includes(r.route_id)).length;
    const what = [
      ...(addedOrders.size ? [t("plan.resetAddedOrders", { n: addedOrders.size })] : []),
      ...(addedRoutes ? [t("plan.resetAddedRoutes", { n: addedRoutes })] : []),
    ].join(t("plan.and"));
    const confirm = plan.origin === "hand" ? t("plan.startOverConfirm") : t("plan.resetConfirm");
    const message = what ? `${confirm} ${t("plan.resetConfirmAdded", { what })}` : confirm;
    if (busy.current || !window.confirm(message)) return;
    busy.current = true;
    setRequest("validating");
    setActionError(null);
    try {
      adopt(await resetPlan(plan.id, plan.version));
      setSelection(null);
      setAnnouncement(t("plan.announceReset"));
    } catch (e) {
      setActionError({
        kind: e instanceof ApiError && e.status === 409 ? "conflict" : "failed",
        message: errText(e),
      });
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  // The plan as saved, as the dispatcher's workbook, headers in the page's language.
  async function exportWorkbook() {
    setActionError(null);
    try {
      const { blob, filename } = await downloadPlanWorkbook(plan.id, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionError({ kind: "failed", message: errText(e) });
    }
  }

  async function close() {
    if (busy.current || !window.confirm(t("plan.closeConfirm"))) return;
    busy.current = true;
    setRequest("loading");
    setActionError(null);
    try {
      adopt(await closePlan(plan.id, plan.version));
      setAnnouncement(t("plan.phase.closed"));
    } catch (e) {
      setActionError({
        kind: e instanceof ApiError && e.status === 409 ? "conflict" : "failed",
        message: errText(e),
      });
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  async function refreshDeliveryStatus() {
    if (busy.current) return;
    busy.current = true;
    setRequest("loading");
    setActionError(null);
    try {
      adopt(await refreshDelivery(plan.id));
      setAnnouncement(t("plan.announceDelivery"));
    } catch (e) {
      setActionError({
        kind: e instanceof ApiError && e.status === 409 ? "conflict" : "failed",
        message: errText(e),
      });
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  async function reloadLatest() {
    if (busy.current) return;
    busy.current = true;
    setRequest("loading");
    try {
      adopt(await getPlan(plan.id));
      setActionError(null);
    } catch (e) {
      setActionError({ kind: "failed", message: errText(e) });
    } finally {
      busy.current = false;
      setRequest("idle");
    }
  }

  async function publish(acknowledge: boolean) {
    if (busy.current) return;
    busy.current = true;
    setRequest("publishing");
    setActionError(null);
    try {
      const next = await publishPlan(plan.id, plan.version, acknowledge);
      adopt(next);
      setAnnouncement(t(`plan.phase.${next.status}`));
    } catch (e) {
      setActionError({ kind: "failed", message: errText(e) });
      // The request may have got through before failing (or the server
      // re-validated and refused): show what the server holds now rather
      // than guessing, so a retry never double-sends.
      try {
        adopt(await getPlan(plan.id));
      } catch {
        /* keep the error visible; the dispatcher can retry */
      }
    } finally {
      busy.current = false;
      setRequest("idle");
      setDialogOpen(false);
      pendingFocus.current = "plan-publish-btn";
    }
  }

  async function withdraw() {
    if (busy.current) return;
    busy.current = true;
    setRequest("loading");
    setActionError(null);
    try {
      adopt(await withdrawPlan(plan.id, plan.version));
      setAnnouncement(t("plan.announceWithdrawn"));
    } catch (e) {
      setActionError({
        kind: e instanceof ApiError && e.status === 409 ? "conflict" : "failed",
        message: errText(e),
      });
      // As after a failed publish: show what the server holds now.
      try {
        adopt(await getPlan(plan.id));
      } catch {
        /* keep the error visible; the dispatcher can retry */
      }
    } finally {
      busy.current = false;
      setRequest("idle");
      setWithdrawOpen(false);
      pendingFocus.current = "plan-withdraw-btn";
    }
  }

  // Leaving with unsaved edits asks first, wherever the page goes.
  function leave(to: () => void) {
    if (dirty && !window.confirm(t("plan.discardConfirm"))) return;
    to();
  }

  function discard() {
    if (window.confirm(t("plan.discardConfirm"))) {
      setWorking(workingFrom(plan));
      setActionError(null);
    }
  }

  function focusIssue(issue: PlanIssue) {
    if (!issue.route_id) return;
    setSelection({ routeId: issue.route_id, zip: issue.zip ?? undefined });
    const order = issue.params.order_no;
    pendingFocus.current =
      typeof order === "string"
        ? orderDomId(issue.route_id, order)
        : issue.zip
          ? stopDomId(issue.route_id, issue.zip)
          : routeDomId(issue.route_id);
    // Focus runs after the next render; make sure there is one.
    setAnnouncement(issueText(issue, t, workingDoc));
  }

  function toggleExpanded(key: string) {
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const fleetRoutes = activeRoutes.filter((r) => r.carrier !== "3pl");
  const lanes = activeRoutes.filter((r) => r.carrier === "3pl");
  const stopCount = (rs: PlanRoute[]) => rs.reduce((n, r) => n + routeStops(r).length, 0);
  const palletCount = (rs: PlanRoute[]) =>
    rs.reduce((n, r) => n + r.orders.reduce((m, o) => m + o.shipment_ids.length, 0), 0);
  // Oversize pallets ship alone by 3PL: counted with the 3PL pallets.
  const threeplPallets = palletCount(lanes) + plan.direct_3pl.length;
  const threeplCost = lanes.reduce((n, r) => n + (timings[r.route_id]?.cost_usd ?? 0), 0);
  const split = (fleet: number, n: number) =>
    n ? t("plan.with3pl", { fleet, n }) : String(fleet);
  const usedVehicles = new Set(fleetRoutes.map((r) => r.vehicle_id).filter(Boolean));
  const usedDrivers = new Set(fleetRoutes.map((r) => r.driver_id).filter(Boolean));
  const idleVehicles = plan.roster.vehicles.filter((v) => !usedVehicles.has(v.id)).length;
  const idleDrivers = plan.roster.drivers.filter(
    (d) => d.available && !usedDrivers.has(d.id),
  ).length;
  const counts = validation?.counts ?? { blocking: 0, warning: 0, info: 0 };
  // Where each order of the plan is, for the order selector.
  const onPlan = new Map<string, string>([
    ...plan.direct_3pl.map((s) => [s.order_no, t("plan.direct3pl")] as const),
    ...unrouted.map((o) => [o.order_no, t("plan.unroutedTitle")] as const),
    ...routes.flatMap((r) => r.orders.map((o) => [o.order_no, labelOf(r.route_id)] as const)),
  ]);

  const validationStat =
    phase === "modified"
      ? t("plan.validationStale")
      : phase === "validating"
        ? t("plan.validating")
        : !validation
          ? t("plan.notValidated")
          : validation.status === "error"
            ? t("plan.validationFailedShort")
            : t("plan.validationCounts", { b: counts.blocking, w: counts.warning });

  // ── Rendering helpers (closures over the plan) ─────────────────────────

  // An unrouted order's window: the dispatcher's, else its pallets' earliest.
  function poolWindow(orderNo: string, rows: PlanShipment[]): PlanWindow {
    const earliest = (values: (number | null | undefined)[]) => {
      const present = values.filter((m): m is number => m !== null && m !== undefined);
      return present.length ? Math.min(...present) : null;
    };
    return (
      workingDoc.windows[orderNo] ?? {
        after_min: earliest(rows.map((r) => r.after_min)),
        deadline_min: earliest(rows.map((r) => r.deadline_min)),
      }
    );
  }

  function describeWindow(w: PlanWindow): string {
    return (
      [
        w.after_min !== null && t("plan.systemNotBefore", { time: formatClock(w.after_min) }),
        w.deadline_min !== null && t("plan.systemDueBy", { time: formatClock(w.deadline_min) }),
      ]
        .filter(Boolean)
        .join(", ") || t("plan.noWindow")
    );
  }

  function orderShipments(order: PlanOrder): PlanShipment[] {
    return order.shipment_ids
      .map((id) => shipments.get(id))
      .filter((s): s is PlanShipment => s !== undefined);
  }

  const customerName = (orderNo: string) => plan.customers[orderNo] ?? orderNo;
  // Why an order sits at its ZIP's centre: as the order sync knows it, or
  // an address (an upload's, a manual order's) that was never placed on the map.
  const zipReason = (row: PlanShipment | undefined) =>
    t(
      `plan.zipReason.${
        row?.address_status && row.address_status !== "address"
          ? row.address_status
          : row?.address
            ? "unplaced"
            : "unknown"
      }`,
    );
  const totalKg = (rows: PlanShipment[]) =>
    `${Math.round(rows.reduce((w, s) => w + s.weight_kg, 0)).toLocaleString()} kg`;

  // The orders on no route: the plan's order pool, with what to decide each
  // one's trip by, and a way to put several on one trip at once.
  const pickedInPool = unrouted.filter((o) => pooled.has(o.order_no)).map((o) => o.order_no);
  const unroutedSection = unrouted.length > 0 && (
    <section
      id="plan-unrouted"
      tabIndex={-1}
      className="section-block plan-unrouted"
      aria-label={t("plan.unroutedTitle")}
    >
      <div className="section-header">
        <h3>{t("plan.unroutedTitle")}</h3>
        {editable && openRoutes.length > 0 && (
          <select
            aria-label={t("plan.putOnTrip")}
            value=""
            disabled={pickedInPool.length === 0}
            onChange={(e) => e.target.value && putBackMany(pickedInPool, e.target.value)}
          >
            <option value="">{t("plan.putOnTripN", { n: pickedInPool.length })}</option>
            {openRoutes.map((r) => (
              <option key={r.route_id} value={r.route_id}>
                {labelOf(r.route_id)}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="plan-muted">
        {plan.origin === "hand" ? t("plan.unroutedPoolNote") : t("plan.unroutedNote")}
      </p>
      <table className="manifest">
        <thead>
          <tr>
            {editable && (
              <th>
                <input
                  type="checkbox"
                  aria-label={t("plan.pickAllUnrouted")}
                  checked={pickedInPool.length === unrouted.length}
                  onChange={(e) =>
                    setPooled(new Set(e.target.checked ? unrouted.map((o) => o.order_no) : []))
                  }
                />
              </th>
            )}
            <th>{t("plan.col.order")}</th>
            <th>{t("plan.col.customer")}</th>
            <th>{t("plan.col.zip")}</th>
            <th>{t("plan.col.address")}</th>
            <th>{t("plan.col.window")}</th>
            <th>{t("plan.col.pallets")}</th>
            <th>{t("plan.col.weight")}</th>
            <th>{t("plan.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {unrouted.map((order, i) => {
            const rows = orderShipments(order);
            const first = rows[0];
            const address = workingDoc.addresses[order.order_no] ?? first?.address ?? null;
            return (
              <tr key={order.order_no}>
                {editable && (
                  <td>
                    <input
                      type="checkbox"
                      aria-label={t("plan.pickUnrouted", { order: order.order_no })}
                      checked={pooled.has(order.order_no)}
                      onChange={() =>
                        setPooled((prev) => {
                          const next = new Set(prev);
                          if (!next.delete(order.order_no)) next.add(order.order_no);
                          return next;
                        })
                      }
                    />
                  </td>
                )}
                <td>{order.order_no}</td>
                <td>{customerName(order.order_no)}</td>
                <td>{order.zip}</td>
                <td>
                  {address ?? t("plan.zipOnlyShort", { zip: order.zip })}
                  {first?.address_status !== "address" && (
                    <div className="plan-muted">{zipReason(first)}</div>
                  )}
                </td>
                <td>{describeWindow(poolWindow(order.order_no, rows))}</td>
                <td>{rows.length}</td>
                <td>{totalKg(rows)}</td>
                <td>
                  {editable && (
                    <select
                      aria-label={t("plan.putBackOn", { order: order.order_no })}
                      value=""
                      onChange={(e) => e.target.value && putBack(i, e.target.value)}
                    >
                      <option value="">{t("plan.putBackPlaceholder")}</option>
                      {openRoutes.map((r) => (
                        <option key={r.route_id} value={r.route_id}>
                          {labelOf(r.route_id)}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );

  // How a drop went, as the provider last reported it.
  // What the plan's last delivery read saw at a drop. Once the order's status
  // shows, that says where it stands, and the read adds only its times.
  function renderDelivery(drop: DeliveryDrop, statusShown: boolean) {
    const state = !statusShown
      ? drop.state
      : drop.arrived_at
        ? drop.departed_at
          ? "departed"
          : "arrived"
        : null;
    return (
      <div className="plan-delivery">
        {state && (
          <span>
            {t(`plan.delivery.${state}`, {
              arrived: clockAt(drop.arrived_at, plan.timezone),
              departed: clockAt(drop.departed_at, plan.timezone),
            })}
          </span>
        )}
        {drop.task_status && <span>{t(`plan.pod.${drop.task_status}`)}</span>}
        {drop.photos.length > 0 && (
          <span className="plan-delivery-photos" aria-label={t("plan.podPhotos")}>
            {drop.photos.map((photo, k) =>
              photo.url ? (
                <a key={k} href={photo.url} target="_blank" rel="noreferrer">
                  {photo.label}
                </a>
              ) : (
                <span key={k}>{t("plan.photoProcessing", { label: photo.label })}</span>
              ),
            )}
          </span>
        )}
      </div>
    );
  }

  function issueList(list: PlanIssue[]) {
    return (
      list.length > 0 && (
        <ul className="plan-issue-inline">
          {list.map((i, k) => (
            <li key={k} className={i.severity}>
              {issueText(i, t, workingDoc)}
            </li>
          ))}
        </ul>
      )
    );
  }

  // An order's delivery address: the dispatcher's (in the working copy) or the
  // system's, with an editor in place while one is being typed.
  function renderAddress(
    route: PlanRoute,
    orderNo: string,
    first: PlanShipment | undefined,
    zip: string,
    key: string,
    changeable: boolean,
  ) {
    const mine = working!.document.addresses[orderNo];
    const saved = plan.document.addresses[orderNo];
    // What the system has: the address now, unless the saved one is the dispatcher's.
    const system = first?.address_edited ? (first.system_address ?? null) : (first?.address ?? null);
    const shown = mine ?? system;
    const buttonId = `plan-address-${route.route_id}-${orderNo}`;

    if (addressDraft?.key === key) {
      const close = () => {
        setAddressDraft(null);
        pendingFocus.current = buttonId;
      };
      return (
        <form
          className="plan-address-edit"
          onSubmit={(e) => {
            e.preventDefault();
            edit(
              { kind: "setAddress", orderNo, address: addressDraft.text },
              t("plan.announceAddress", { order: orderNo }),
            );
            close();
          }}
        >
          <input
            aria-label={t("plan.addressOf", { order: orderNo })}
            value={addressDraft.text}
            autoFocus
            maxLength={300}
            onChange={(e) => setAddressDraft({ key, text: e.target.value })}
            onKeyDown={(e) => e.key === "Escape" && close()}
          />
          <button type="submit" className="btn btn-ghost plan-icon-btn">
            {t("plan.addressSave")}
          </button>
        </form>
      );
    }
    return (
      <>
        {shown ?? <span>{t("plan.zipOnlyShort", { zip })}</span>}
        {mine !== undefined && <span className="plan-tag is-added">{t("plan.addressEdited")}</span>}
        {mine !== saved ? (
          <div className="plan-muted">{t("plan.addressUnsaved")}</div>
        ) : (
          first?.location_precision === "zip" && (
            <div className="plan-muted">{zipReason(first)}</div>
          )
        )}
        {mine !== undefined && system && (
          <div className="plan-muted">{t("plan.systemAddress", { address: system })}</div>
        )}
        {mine === saved && first?.system_dest_zip && (
          <div className="plan-muted">
            {t("plan.addressMovedZip", { zip: first.dest_zip, own: first.system_dest_zip })}
          </div>
        )}
        {changeable && (
          <div className="plan-address-actions">
            <button
              id={buttonId}
              className="plan-link-btn"
              aria-label={t("plan.editAddress", { order: orderNo })}
              onClick={() => setAddressDraft({ key, text: shown ?? "" })}
            >
              ✎
            </button>
            {mine !== undefined && (
              <button
                className="plan-link-btn"
                onClick={() =>
                  edit(
                    { kind: "setAddress", orderNo, address: null },
                    t("plan.announceAddressBack", { order: orderNo }),
                    buttonId,
                  )
                }
              >
                {t("plan.useSystemAddress")}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // An order's delivery notes for the driver: the dispatcher's (in the working
  // copy; "" clears them) or the system's, with an editor in place while written.
  function renderNotes(
    route: PlanRoute,
    orderNo: string,
    first: PlanShipment | undefined,
    key: string,
    changeable: boolean,
  ) {
    const mine = workingDoc.notes[orderNo];
    // What the system has: the notes now, unless the saved ones are the dispatcher's.
    const system = first?.notes_edited
      ? (first.system_notes ?? null)
      : (first?.delivery_notes ?? null);
    const shown = mine ?? system;
    const buttonId = `plan-notes-${route.route_id}-${orderNo}`;

    if (notesDraft?.key === key) {
      const close = () => {
        setNotesDraft(null);
        pendingFocus.current = buttonId;
      };
      return (
        <form
          className="plan-address-edit plan-notes-edit"
          onSubmit={(e) => {
            e.preventDefault();
            // Refused, never cut: the server holds the same limit.
            if (notesDraft.text.trim().length > MAX_NOTES_CHARS) {
              setNotesDraft({ ...notesDraft, tooLong: true });
              return;
            }
            edit(
              { kind: "setNotes", orderNo, notes: notesDraft.text },
              t("plan.announceNotes", { order: orderNo }),
            );
            close();
          }}
        >
          <textarea
            aria-label={t("plan.notesOf", { order: orderNo })}
            value={notesDraft.text}
            autoFocus
            rows={3}
            onChange={(e) => setNotesDraft({ key, text: e.target.value })}
            onKeyDown={(e) => e.key === "Escape" && close()}
          />
          {notesDraft.tooLong && (
            <p role="alert" className="is-late">
              {t("plan.notesTooLong", {
                max: MAX_NOTES_CHARS.toLocaleString("en-US"),
                n: notesDraft.text.trim().length.toLocaleString("en-US"),
              })}
            </p>
          )}
          <button type="submit" className="btn btn-ghost plan-icon-btn">
            {t("plan.notesSave")}
          </button>
        </form>
      );
    }
    return (
      <>
        {shown ? (
          <span>{shown}</span>
        ) : (
          mine === "" && <span className="plan-muted">{t("plan.noNotes")}</span>
        )}
        {mine !== undefined && <span className="plan-tag is-added">{t("plan.notesEdited")}</span>}
        {mine !== undefined && system && (
          <div className="plan-muted">{t("plan.systemNotes", { notes: system })}</div>
        )}
        {changeable && (
          <div className="plan-address-actions">
            <button
              id={buttonId}
              className="plan-link-btn"
              aria-label={t("plan.editNotes", { order: orderNo })}
              onClick={() => setNotesDraft({ key, text: shown ?? "" })}
            >
              ✎
            </button>
            {mine !== undefined && (
              <button
                className="plan-link-btn"
                onClick={() =>
                  edit(
                    { kind: "setNotes", orderNo, notes: null },
                    t("plan.announceNotesBack", { order: orderNo }),
                    buttonId,
                  )
                }
              >
                {t("plan.useSystemNotes")}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // An order's delivery window: the dispatcher's (in the working copy) or the
  // system's, with an editor in place while one is being set.
  function renderWindow(
    route: PlanRoute,
    orderNo: string,
    rows: PlanShipment[],
    late: boolean,
    key: string,
    changeable: boolean,
  ) {
    const mine = workingDoc.windows[orderNo];
    const saved = plan.document.windows[orderNo];
    const first = rows[0];
    const earliest = (values: (number | null | undefined)[]) => {
      const present = values.filter((m): m is number => m !== null && m !== undefined);
      return present.length ? Math.min(...present) : null;
    };
    // What the system has: the window now, unless the saved one is the dispatcher's.
    const system: PlanWindow = first?.window_edited
      ? {
          after_min: first.system_after_min ?? null,
          deadline_min: first.system_deadline_min ?? null,
        }
      : {
          after_min: earliest(rows.map((s) => s.after_min)),
          deadline_min: earliest(rows.map((s) => s.deadline_min)),
        };
    const shown = mine ?? system;
    const buttonId = `plan-window-${route.route_id}-${orderNo}`;
    const pad = (n: number) => String(n).padStart(2, "0");
    const hhmm = (m: number | null) =>
      m === null ? "" : `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
    const minutes = (s: string) => (s ? Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5)) : null);
    const describe = (w: PlanWindow) =>
      [
        w.after_min !== null && t("plan.systemNotBefore", { time: formatClock(w.after_min) }),
        w.deadline_min !== null && t("plan.systemDueBy", { time: formatClock(w.deadline_min) }),
      ]
        .filter(Boolean)
        .join(", ") || t("plan.noWindow");

    if (windowDraft?.key === key) {
      const close = () => {
        setWindowDraft(null);
        pendingFocus.current = buttonId;
      };
      return (
        <form
          className="plan-window-edit"
          onSubmit={(e) => {
            e.preventDefault();
            const window = {
              after_min: minutes(windowDraft.after),
              deadline_min: minutes(windowDraft.deadline),
            };
            if (
              window.after_min !== null &&
              window.deadline_min !== null &&
              window.after_min > window.deadline_min
            ) {
              setWindowDraft({ ...windowDraft, error: true });
              return;
            }
            edit(
              { kind: "setWindow", orderNo, window },
              t("plan.announceWindow", { order: orderNo }),
            );
            close();
          }}
          onKeyDown={(e) => e.key === "Escape" && close()}
        >
          <label>
            <span className="control-label">{t("plan.windowAfter")}</span>
            <input
              type="time"
              aria-label={t("plan.windowAfterOf", { order: orderNo })}
              value={windowDraft.after}
              autoFocus
              onChange={(e) =>
                setWindowDraft({ ...windowDraft, after: e.target.value, error: false })
              }
            />
          </label>
          <label>
            <span className="control-label">{t("plan.windowDeadline")}</span>
            <input
              type="time"
              aria-label={t("plan.windowDeadlineOf", { order: orderNo })}
              value={windowDraft.deadline}
              onChange={(e) =>
                setWindowDraft({ ...windowDraft, deadline: e.target.value, error: false })
              }
            />
          </label>
          {windowDraft.error && (
            <p role="alert" className="is-late">
              {t("plan.windowBackwards")}
            </p>
          )}
          <button type="submit" className="btn btn-ghost plan-icon-btn">
            {t("plan.windowSave")}
          </button>
        </form>
      );
    }
    return (
      <>
        {shown.after_min !== null && (
          <span>{t("plan.notBefore", { time: formatClock(shown.after_min) })}</span>
        )}
        {shown.deadline_min !== null && (
          <span className={late ? "is-late" : ""}>
            {t("plan.dueBy", { time: formatClock(shown.deadline_min) })}
          </span>
        )}
        {shown.after_min === null && shown.deadline_min === null && (
          <span className="plan-muted">—</span>
        )}
        {mine !== undefined && <span className="plan-tag is-added">{t("plan.windowEdited")}</span>}
        {JSON.stringify(mine) !== JSON.stringify(saved) && (
          <div className="plan-muted">{t("plan.windowUnsaved")}</div>
        )}
        {mine !== undefined && (
          <div className="plan-muted">{t("plan.systemWindow", { window: describe(system) })}</div>
        )}
        {changeable && (
          <div className="plan-address-actions">
            <button
              id={buttonId}
              className="plan-link-btn"
              aria-label={t("plan.editWindow", { order: orderNo })}
              onClick={() =>
                setWindowDraft({
                  key,
                  after: hhmm(shown.after_min),
                  deadline: hhmm(shown.deadline_min),
                  error: false,
                })
              }
            >
              ✎
            </button>
            {mine !== undefined && (
              <button
                className="plan-link-btn"
                onClick={() =>
                  edit(
                    { kind: "setWindow", orderNo, window: null },
                    t("plan.announceWindowBack", { order: orderNo }),
                    buttonId,
                  )
                }
              >
                {t("plan.useSystemWindow")}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // Where a route stands with its driver, as the last publish or clear left it.
  function publicationText(pub: RoutePublication): string {
    if (pub.status === "not_applicable") return t("plan.routeNotSent3pl");
    if (pub.status === "withdrawn") return t("plan.routeWithdrawn");
    if (pub.status === "failed") return t("plan.routeFailed", { error: pub.error ?? "" });
    if (pub.kept_started) return t("plan.routeKeptStarted");
    if (pub.error) return t("plan.routeUpdateFailed", { error: pub.error });
    return t("plan.routeSent");
  }

  function renderRouteHeader(route: PlanRoute) {
    const timing = timings[route.route_id];
    // The route's own issues, and its stops' (an order's sit on its row).
    const routeIssues = dirty
      ? []
      : issues.filter((i) => i.route_id === route.route_id && !i.params.order_no);
    const pub = plan.publication?.routes[route.route_id];
    const label = labelOf(route.route_id);
    const lane = route.carrier === "3pl";
    const changeable = routeEditable(route.route_id);

    return (
      <tr
        id={routeDomId(route.route_id)}
        tabIndex={-1}
        className={`plan-route-row${
          selection?.routeId === route.route_id && !selection.zip ? " is-selected" : ""
        }`}
      >
        <th colSpan={COLUMNS.length} scope="rowgroup">
          <div className="plan-route-head">
            <div className="plan-route-id">
              <button
                className="plan-route-title"
                onClick={() => setSelection({ routeId: route.route_id })}
              >
                {label}
              </button>
              {lane && <span className="plan-3pl-badge">3PL</span>}
              <div className={`truck-route-meta${dirty ? " is-stale" : ""}`}>
                {timing && lane
                  ? `${t("plan.laneTimes", {
                      depart: formatClock(timing.depart_min),
                      last: formatClock(timing.return_min),
                      mi: Math.round(timing.miles),
                      cost: Math.round(timing.cost_usd ?? 0).toLocaleString(),
                    })} · ${t("plan.loadPct", {
                      w: Math.round(timing.weight_pct),
                      a: Math.round(timing.footprint_pct),
                    })}`
                  : timing
                    ? `${t("plan.routeTimes", {
                        start: formatClock(timing.start_min),
                        depart: formatClock(timing.depart_min),
                        back: formatClock(timing.return_min),
                        h: timing.hours.toFixed(1),
                        mi: Math.round(timing.miles),
                      })}${
                        timing.cost_usd == null
                          ? ""
                          : ` · ${t("plan.routeCost", {
                              cost: Math.round(timing.cost_usd).toLocaleString(),
                            })}`
                      } · ${t("plan.loadPct", {
                        w: Math.round(timing.weight_pct),
                        a: Math.round(timing.footprint_pct),
                      })}${
                        timing.lunch_min === null
                          ? ""
                          : ` · ${t("plan.lunchAt", { time: formatClock(timing.lunch_min) })}`
                      }`
                    : route.orders.length
                      ? t("plan.noTiming")
                      : t("plan.emptyRoute")}
                {dirty && timing ? ` · ${t("plan.staleShort")}` : ""}
              </div>
            </div>
            <div className="plan-assign">
              {changeable && removableTrip(plan, workingDoc, route.route_id) && (
                <button
                  className="btn btn-ghost"
                  aria-label={t("plan.removeTripOf", { route: label })}
                  onClick={() =>
                    edit(
                      { kind: "removeTrip", routeId: route.route_id },
                      t("plan.announceTripRemoved", { route: label }),
                      route.truck_id ? truckDomId(route.truck_id) : undefined,
                    )
                  }
                >
                  {t("plan.removeTrip")}
                </button>
              )}
              {changeable && (
                <button
                  className="btn btn-ghost"
                  aria-label={t("plan.addOrdersTo", { route: label })}
                  onClick={() => {
                    setAddError(null);
                    setAdding({ routeId: route.route_id });
                  }}
                >
                  + {t("plan.addOrders")}
                </button>
              )}
            </div>
          </div>

          {started.has(route.route_id) && <p className="plan-lock">{t("plan.routeStarted")}</p>}
          {!route.sequence_locked && changeable && (
            <p className="plan-muted">{t("plan.sequencePlanned")}</p>
          )}
          {route.sequence_locked && (
            <p className="plan-lock">
              {t("plan.locked")}
              {changeable && (
                <button
                  className="plan-link-btn"
                  onClick={() =>
                    edit(
                      { kind: "unlockSequence", routeId: route.route_id },
                      t("plan.announceUnlocked", { route: label }),
                    )
                  }
                >
                  {t("plan.unlock")}
                </button>
              )}
            </p>
          )}
          {timing?.loading &&
            !dirty &&
            (timing.loading.resequenced || LOADING_NOTE[timing.loading.verdict]) && (
              <p className={`plan-loading-note ${timing.loading.verdict}`}>
                {timing.loading.resequenced && `${t("plan.resequenced")} · `}
                {LOADING_NOTE[timing.loading.verdict] && t(LOADING_NOTE[timing.loading.verdict]!)}
              </p>
            )}
          {pub && (
            <p
              className={`plan-route-pub ${pub.status}${
                pub.status === "sent" && (pub.kept_started || pub.error) ? " is-stale" : ""
              }`}
            >
              {publicationText(pub)}
            </p>
          )}
          {!dirty && plan.changes.update.includes(route.route_id) && (
            <p className="plan-route-change">{t("plan.routeChanged")}</p>
          )}
          {!dirty && plan.changes.withdraw.includes(route.route_id) && (
            <p className="plan-route-change">{t("plan.routeToWithdraw")}</p>
          )}
          {issueList(routeIssues)}
        </th>
      </tr>
    );
  }

  function renderOrderRow(
    route: PlanRoute,
    stop: RouteStop,
    position: number,
  ) {
    const { order, index } = stop.orders[position];
    const rows = orderShipments(order);
    const first = rows[0];
    const name = customerName(order.order_no);
    const timing = timings[route.route_id]?.stops.find((s) => s.zip === stop.zip);
    const cust = timing?.customers.find((c) => c.order_no === order.order_no);
    const late = !dirty && (cust?.late_min ?? 0) > 0;
    const orderIssues = dirty
      ? []
      : issues.filter(
          (i) => i.route_id === route.route_id && i.params.order_no === order.order_no,
        );
    const key = `${route.route_id}:${order.order_no}`;
    const changeable = routeEditable(route.route_id);
    const progress = plan.delivery?.routes[route.route_id]?.find((d) =>
      d.order_nos.includes(order.order_no),
    );
    const status = statuses[key];
    const statusShown = status !== undefined && status !== "planned";
    const isOpen = expanded.has(key);
    // The stop's first row carries its number; the stop moves from there.
    const leadsStop = position === 0;

    return (
      <Fragment key={order.order_no}>
        <tr
          id={orderDomId(route.route_id, order.order_no)}
          className={`plan-order-row${dropTarget === key ? " is-drop-target" : ""}${
            leadsStop ? " leads-stop" : ""
          }`}
          tabIndex={-1}
          aria-label={t("plan.orderAria", { order: order.order_no, name })}
          aria-current={
            selection?.routeId === route.route_id && selection.zip === stop.zip ? "true" : undefined
          }
          draggable={
            changeable &&
            addressDraft?.key !== key &&
            windowDraft?.key !== key &&
            notesDraft?.key !== key
          }
          onClick={() => setSelection({ routeId: route.route_id, zip: stop.zip })}
          onDragStart={(e) => {
            drag.current = { routeId: route.route_id, index };
            e.dataTransfer?.setData("text/plain", key);
            if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(e) => dragOver(e, key)}
          onDrop={(e) => dropOnRow(e, route, index)}
          onDragEnd={endDrag}
        >
          <td
            className="plan-stop-cell"
            id={leadsStop ? stopDomId(route.route_id, stop.zip) : undefined}
            tabIndex={leadsStop ? -1 : undefined}
          >
            <span className={leadsStop ? "" : "plan-muted"}>{index + 1}</span>
          </td>
          <td>
            <span className="plan-order-no">{order.order_no}</span>
            {first?.manual ? (
              <span className="plan-tag is-added">{t("plan.badgeManual")}</span>
            ) : first?.added ? (
              <span className="plan-tag is-added">{t("plan.badgeAdded")}</span>
            ) : null}
            {rows.some((s) => !s.truck_53_ok) && <span className="plan-tag">{t("plan.no53")}</span>}
            {issueList(orderIssues)}
          </td>
          <td>{name}</td>
          <td>{stop.zip}</td>
          <td className="num">{rows.length}</td>
          <td className="num">{totalKg(rows)}</td>
          <td
            className="plan-window"
            onClick={(e) => windowDraft?.key === key && e.stopPropagation()}
          >
            {renderWindow(route, order.order_no, rows, late, key, changeable)}
          </td>
          <td className={`num${dirty ? " is-stale" : ""}`}>
            <span className={late ? "is-late" : ""}>{formatClock(cust?.eta_min ?? timing?.eta_min)}</span>
            {late && <span className="is-late plan-late">{t("plan.lateBy", { m: cust!.late_min })}</span>}
          </td>
          <td className="plan-address" onClick={(e) => addressDraft?.key === key && e.stopPropagation()}>
            {renderAddress(route, order.order_no, first, stop.zip, key, changeable)}
          </td>
          <td
            className="plan-notes"
            onClick={(e) => notesDraft?.key === key && e.stopPropagation()}
          >
            {renderNotes(route, order.order_no, first, key, changeable)}
          </td>
          <td>
            {statusShown && (
              <span className={`plan-chip ${STATUS_TONE[status]}`} data-testid="order-status">
                {t(`status.${status}`)}
              </span>
            )}
            {progress && renderDelivery(progress, statusShown)}
          </td>
          <td className="plan-row-actions" onClick={(e) => e.stopPropagation()}>
            {changeable &&
              ([-1, 1] as const).map((delta) => (
                <button
                  key={delta}
                  id={stepDomId(route.route_id, order.order_no, delta)}
                  className="btn btn-ghost plan-icon-btn"
                  aria-label={t(delta < 0 ? "plan.moveEarlier" : "plan.moveLater", {
                    order: order.order_no,
                  })}
                  title={t(delta < 0 ? "plan.moveEarlier" : "plan.moveLater", {
                    order: order.order_no,
                  })}
                  disabled={!stepEdit(route, index, delta)}
                  onClick={() => step(route, index, delta)}
                >
                  {delta < 0 ? "↑" : "↓"}
                </button>
              ))}
            {changeable && (
              <>
                <select
                  aria-label={t("plan.routeFor", { order: order.order_no })}
                  value={route.route_id}
                  onChange={(e) =>
                    e.target.value !== route.route_id && moveOrder(route, index, e.target.value)
                  }
                >
                  {openRoutes.map((r) => (
                    <option key={r.route_id} value={r.route_id}>
                      {labelOf(r.route_id)}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-ghost plan-icon-btn"
                  aria-label={t("plan.takeOff", { order: order.order_no })}
                  title={t("plan.takeOff", { order: order.order_no })}
                  onClick={() => takeOff(route, index)}
                >
                  ✕
                </button>
              </>
            )}
            <button
              className="btn btn-ghost plan-icon-btn"
              aria-expanded={isOpen}
              aria-label={t(isOpen ? "plan.hidePallets" : "plan.showPallets", {
                order: order.order_no,
              })}
              title={t(isOpen ? "plan.hidePallets" : "plan.showPallets", {
                order: order.order_no,
              })}
              onClick={() => toggleExpanded(key)}
            >
              {isOpen ? "▴" : "▾"}
            </button>
          </td>
        </tr>
        {isOpen && (
          <tr className="plan-order-detail">
            <td />
            <td colSpan={COLUMNS.length - 1}>
              <table
                className="manifest"
                aria-label={t("plan.palletsOf", { order: order.order_no })}
              >
                <thead>
                  <tr>
                    <th>{t("plan.pallet")}</th>
                    <th>{t("plan.dims")}</th>
                    <th>{t("plan.weight")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.shipment_id}>
                      <td>
                        {p.shipment_id}
                        {!p.truck_53_ok && <span className="plan-tag"> {t("plan.no53")}</span>}
                      </td>
                      <td>
                        {p.length_ft.toFixed(1)}×{p.width_ft.toFixed(1)}×{p.height_ft.toFixed(1)} ft
                      </td>
                      <td>{Math.round(p.weight_kg).toLocaleString()} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        )}
      </Fragment>
    );
  }

  // A truck's own row group: its vehicle and driver, which all its trips run
  // on, and adding trips to it. Its trips follow, in its colour.
  function renderTruck(truck: PlanTruck) {
    const id = truck.truck_id;
    const trips = routes.filter((r) => r.truck_id === id);
    // A trip its driver has started keeps its vehicle, so its truck does too.
    const changeable = editable && !trips.some((r) => started.has(r.route_id));
    const vehicles = freeVehicles(workingDoc, plan.roster.vehicles, id);
    const drivers = plan.roster.drivers;
    const name = t("plan.truck", { truck: id });
    const lanes = truck.carrier === "3pl";
    return (
      <tbody
        key={`truck-${id}`}
        aria-label={name}
        className={`plan-truck-group${lanes ? " is-3pl" : ""}`}
        style={
          { ["--truck-color" as string]: lanes ? THREEPL_COLOR : truckColor(id) } as CSSProperties
        }
      >
        <tr id={truckDomId(id)} tabIndex={-1} className="plan-truck-row">
          <th colSpan={COLUMNS.length} scope="rowgroup">
            <div className="plan-route-head">
              <div className="plan-route-id">
                <span className="plan-truck-title">{name}</span>
                {lanes && <span className="plan-3pl-badge">3PL</span>}
                <div className="truck-route-meta">{t("plan.truckTrips", { n: trips.length })}</div>
              </div>
              <div className="plan-assign">
                <label className="control">
                  <span className="control-label">{t("plan.vehicle")}</span>
                  <select
                    value={lanes ? "3pl" : (truck.vehicle_id ?? "")}
                    disabled={!changeable}
                    onChange={(e) => setTruckVehicle(truck, e.target.value || null)}
                  >
                    <option value="">— {t("plan.unassigned")} —</option>
                    <option value="3pl">{t("plan.carrier3pl")}</option>
                    {truck.vehicle_id && !vehicles.some((v) => v.id === truck.vehicle_id) && (
                      <option value={truck.vehicle_id}>{truck.vehicle_id}</option>
                    )}
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} · {v.trailer_type.replace("ft", "′")}
                      </option>
                    ))}
                  </select>
                </label>
                {!lanes && (
                <label className="control">
                  <span className="control-label">{t("plan.driver")}</span>
                  <select
                    value={truck.driver_id ?? ""}
                    disabled={!changeable}
                    onChange={(e) =>
                      edit(
                        { kind: "setDriver", truckId: id, driverId: e.target.value || null },
                        t("plan.announceDriver", {
                          route: id,
                          d: driverName(e.target.value || null),
                        }),
                      )
                    }
                  >
                    <option value="">— {t("plan.unassigned")} —</option>
                    {truck.driver_id && !drivers.some((d) => d.id === truck.driver_id) && (
                      <option value={truck.driver_id}>{truck.driver_id}</option>
                    )}
                    {drivers.map((d) => (
                      <option
                        key={d.id}
                        value={d.id}
                        disabled={!d.available && d.id !== truck.driver_id}
                      >
                        {d.name}
                        {d.available ? "" : ` (${t("plan.offToday")})`}
                      </option>
                    ))}
                  </select>
                </label>
                )}
                <label className="control">
                  <span className="control-label">{t("plan.departs")}</span>
                  <select
                    value={truck.depart_min ?? DEFAULT_DEPARTURE_MIN}
                    disabled={!changeable}
                    onChange={(e) =>
                      edit(
                        {
                          kind: "setDeparture",
                          truckId: id,
                          departMin: Number(e.target.value),
                        },
                        t("plan.announceDeparture", {
                          truck: id,
                          at: formatClock(Number(e.target.value)),
                        }),
                      )
                    }
                  >
                    {DEPARTURE_SLOTS.map((m) => (
                      <option key={m} value={m}>
                        {formatClock(m)}
                      </option>
                    ))}
                  </select>
                </label>
                {editable && (
                  <button
                    className="btn btn-ghost"
                    aria-label={t("plan.addTripTo", { truck: id })}
                    onClick={() => addTrip(id)}
                  >
                    + {t("plan.addTrip")}
                  </button>
                )}
                {editable && removableTruck(plan, workingDoc, id) && (
                  <button
                    className="btn btn-ghost"
                    aria-label={t("plan.removeTruckOf", { truck: id })}
                    onClick={() =>
                      edit(
                        { kind: "removeTruck", truckId: id },
                        t("plan.announceTruckRemoved", { truck: id }),
                        "plan-title",
                      )
                    }
                  >
                    {t("plan.removeTruck")}
                  </button>
                )}
              </div>
            </div>
          </th>
        </tr>
      </tbody>
    );
  }

  function renderRoute(route: PlanRoute) {
    const lane = route.carrier === "3pl";
    const stops = routeStops(route);
    const emptyKey = `${route.route_id}:empty`;
    return (
      <tbody
        key={route.route_id}
        aria-label={labelOf(route.route_id)}
        className={`plan-route-group${lane ? " is-3pl" : ""}`}
        style={{ ["--truck-color" as string]: routeColor(route) } as CSSProperties}
      >
        {renderRouteHeader(route)}
        {stops.flatMap((stop) =>
          stop.orders.map((_, position) => renderOrderRow(route, stop, position)),
        )}
        {stops.length === 0 && (
          <tr
            className={`plan-empty-row${dropTarget === emptyKey ? " is-drop-target" : ""}`}
            onDragOver={(e) => dragOver(e, emptyKey)}
            onDrop={(e) => dropOnRow(e, route)}
          >
            <td colSpan={COLUMNS.length} className="plan-muted">
              {t("plan.emptyRouteRow")}
            </td>
          </tr>
        )}
      </tbody>
    );
  }

  // Routes the last publish or clear didn't bring in line: not sent, or still
  // as their driver last got them (started, or the provider refused the change).
  const heldRoutes = Object.entries(plan.publication?.routes ?? {}).flatMap(([rid, r]) =>
    r.status === "failed" || (r.status === "sent" && (r.kept_started || r.error))
      ? [[rid, r] as const]
      : [],
  );
  // A route its driver has started stays as they have it: a retry won't change it.
  const retryHelps = toSend.length > 0 || heldRoutes.some(([, r]) => !r.kept_started);

  return (
    <main className="results plan-page">
      <div className="results-toolbar plan-toolbar">
        <div>
          <button className="btn btn-ghost plan-back" onClick={() => leave(onBack)}>
            {t("plan.back")}
          </button>
          {onSavedPlans && (
            <button className="btn btn-ghost plan-back" onClick={() => leave(onSavedPlans)}>
              {t("plan.savedPlans")}
            </button>
          )}
          {onTrackDeliveries && (
            <button
              className="btn btn-ghost plan-back"
              onClick={() => leave(() => onTrackDeliveries(plan.depot_zip, plan.plan_date))}
            >
              {t("plan.trackDeliveries")}
            </button>
          )}
          <h2 id="plan-title" tabIndex={-1}>
            {t("plan.title")}
          </h2>
          <div className="meta">
            {t("plan.meta", { depot: plan.depot_label, v: plan.version })} ·{" "}
            <span className="plan-tag" data-testid="plan-origin">
              {plan.origin === "hand" ? t("plan.originHand") : t("plan.originRun")}
            </span>
          </div>
          {plan.delivery && (
            <div className="meta">
              {t("plan.deliveryAsOf", {
                time: clockAt(plan.delivery.refreshed_at, plan.timezone),
              })}
            </div>
          )}
        </div>
        <div className="toolbar-actions">
          {dirty && editable && (
            <button className="btn btn-ghost" onClick={discard}>
              {t("plan.discard")}
            </button>
          )}
          {plan.can_edit && (
            <button
              className="btn btn-ghost"
              onClick={() => void reset()}
              disabled={request !== "idle" || (plan.at_baseline && !dirty)}
              title={plan.at_baseline && !dirty ? t("plan.atBaselineTip") : undefined}
            >
              {plan.origin === "hand" ? t("plan.startOver") : t("plan.reset")}
            </button>
          )}
          {plan.can_edit && (
            <button
              className="btn btn-secondary"
              onClick={() => void revalidate()}
              disabled={request !== "idle"}
            >
              {request === "validating" ? t("plan.validating") : t("plan.revalidate")}
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={() => void exportWorkbook()}
            disabled={request !== "idle" || dirty}
            title={dirty ? t("plan.exportSaveFirst") : undefined}
          >
            {t("plan.exportExcel")}
          </button>
          {plan.can_refresh_delivery && (
            <button
              className="btn btn-ghost"
              onClick={() => void refreshDeliveryStatus()}
              disabled={request !== "idle"}
            >
              {t("plan.refreshDelivery")}
            </button>
          )}
          {plan.can_close && (
            <button
              className="btn btn-ghost"
              onClick={() => void close()}
              disabled={request !== "idle"}
            >
              {t("plan.close")}
            </button>
          )}
          {plan.can_withdraw && (
            <button
              id="plan-withdraw-btn"
              className="btn btn-danger"
              onClick={() => setWithdrawOpen(true)}
              disabled={request !== "idle" || dirty}
            >
              {t("plan.withdraw")}
            </button>
          )}
          {plan.status !== "closed" && (
            <button
              id="plan-publish-btn"
              className="btn btn-primary"
              onClick={() => setDialogOpen(true)}
              disabled={!publishable}
              title={
                publishable
                  ? undefined
                  : sentCount > 0 && !dirty && changeCount(plan) === 0
                    ? t("plan.noChangesTip")
                    : t("plan.publishDisabledTip")
              }
            >
              {request === "publishing"
                ? t("plan.publishing")
                : publishMode === "retry"
                  ? t("plan.retryFailed")
                  : publishMode === "changes"
                    ? t("plan.publishChanges")
                    : t("plan.publish")}
            </button>
          )}
        </div>
      </div>

      <section className="section-block plan-summary" aria-label={t("plan.summary")}>
        <div className="plan-summary-grid">
          <div className="plan-stat">
            <span className="control-label">{t("plan.date")}</span>
            {editable ? (
              <input
                type="date"
                className="plan-date"
                aria-label={t("plan.date")}
                value={working.plan_date}
                onChange={(e) =>
                  e.target.value && setWorking({ ...working, plan_date: e.target.value })
                }
              />
            ) : (
              <span className="plan-stat-value">{working.plan_date}</span>
            )}
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.status")}</span>
            <span className={`plan-chip ${PHASE_TONE[phase]}`} data-testid="plan-phase">
              {t(`plan.phase.${phase}`)}
            </span>
            <span className="plan-muted">
              {t("plan.lastSaved", {
                time: clockOnDay(plan.updated_at, plan.timezone, todayIn(plan.timezone)),
              })}
            </span>
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.routes")}</span>
            <span className="plan-stat-value">{split(fleetRoutes.length, lanes.length)}</span>
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.stops")}</span>
            <span className="plan-stat-value">
              {split(stopCount(fleetRoutes), stopCount(lanes))}
            </span>
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.shipments")}</span>
            <span className="plan-stat-value">
              {split(palletCount(fleetRoutes), threeplPallets)}
            </span>
          </div>
          {lanes.length > 0 && (
            <div className="plan-stat">
              <span className="control-label">{t("plan.threeplCost")}</span>
              <span className={`plan-stat-value${dirty ? " is-stale" : ""}`}>
                ${Math.round(threeplCost).toLocaleString()}
              </span>
            </div>
          )}
          <div className="plan-stat">
            <span className="control-label">{t("plan.vehicles")}</span>
            <span className="plan-stat-value">
              {fleetRoutes.filter((r) => r.vehicle_id).length}/{fleetRoutes.length}
            </span>
            <span className="plan-stat-sub">{t("plan.idle", { n: idleVehicles })}</span>
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.drivers")}</span>
            <span className="plan-stat-value">
              {fleetRoutes.filter((r) => r.driver_id).length}/{fleetRoutes.length}
            </span>
            <span className="plan-stat-sub">{t("plan.idleDrivers", { n: idleDrivers })}</span>
          </div>
          <div className="plan-stat">
            <span className="control-label">{t("plan.validation")}</span>
            <span className="plan-stat-value plan-stat-small">{validationStat}</span>
          </div>
        </div>
      </section>

      {actionError && (
        <div className="banner warn" role="alert">
          {actionError.kind === "conflict"
            ? t("plan.conflict", { message: actionError.message })
            : actionError.kind === "rejected"
              ? t("plan.rejected")
              : t("plan.requestFailed", { message: actionError.message })}
          {actionError.errors && actionError.errors.length > 0 && (
            <ul>
              {actionError.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          <div className="plan-banner-actions">
            {actionError.kind === "conflict" ? (
              <button className="btn btn-secondary" onClick={() => void reloadLatest()}>
                {t("plan.reloadLatest")}
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={() => void reloadLatest()}>
                {t("plan.refresh")}
              </button>
            )}
          </div>
        </div>
      )}

      {plan.status === "published" && (
        <div className="banner plan-banner ok" role="status">
          {t("plan.publishedBanner", {
            at: plan.published_at ? new Date(plan.published_at).toLocaleString() : "",
            by: plan.published_by_email ?? "—",
          })}
          {!dirty && changeCount(plan) === 0 && ` ${t("plan.noChangesTip")}`}
        </div>
      )}
      {changesPending && (
        <div className="banner plan-banner caution" role="status">
          {t("plan.changesPending")}
        </div>
      )}
      {plan.status === "closed" && (
        <div className="banner plan-banner" role="status">
          {t("plan.closedBanner", {
            at: plan.publication?.closed
              ? new Date(plan.publication.closed.at).toLocaleString()
              : "",
            by: plan.publication?.closed?.email ?? "—",
            sent: sentCount,
            total: sentCount + toSend.length,
          })}
        </div>
      )}
      {retrying && (
        <div className="banner plan-banner caution" role="status">
          {t(retryHelps ? "plan.partialBanner" : "plan.partialBannerStarted", {
            sent: sentCount,
            total: sentCount + toSend.length,
          })}
          {heldRoutes.length > 0 && (
            <ul>
              {heldRoutes.map(([rid, r]) => (
                <li key={rid}>
                  {labelOf(rid)}:{" "}
                  {r.status === "failed" ? (r.error ?? t("plan.unknownError")) : publicationText(r)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {nothingToPublish && (
        <div className="banner plan-banner caution" role="status">
          {t("plan.nothingToPublish")}
        </div>
      )}
      {plan.status === "publishing" && request !== "publishing" && (
        <div className="banner plan-banner caution" role="status">
          {plan.publish_stuck ? t("plan.stuckBanner") : t("plan.inProgressBanner")}
        </div>
      )}
      {validation?.status === "error" && !dirty && (
        <div className="banner warn" role="alert">
          {t("plan.validationFailed", { message: validation.message ?? "" })}
          <div className="plan-banner-actions">
            {plan.can_edit && (
              <button className="btn btn-secondary" onClick={() => void revalidate()}>
                {t("plan.retry")}
              </button>
            )}
          </div>
        </div>
      )}

      {mapData && (
        <section
          className={`section-block plan-map${dirty ? " is-stale" : ""}`}
          aria-label={t("plan.map")}
        >
          <details open>
            <summary>{t("plan.map")}</summary>
            {dirty && <p className="plan-stale-note">{t("plan.mapStale")}</p>}
            <RouteMap
              result={mapData}
              highlight={selection?.routeId ?? null}
              highlightStop={selection?.zip ?? null}
              onSelect={(routeId, zip) => setSelection({ routeId, zip })}
              unrouted={mapData.unrouted}
            />
          </details>
        </section>
      )}

      <section className="section-block plan-validation" aria-labelledby="plan-validation-h">
        <div className="section-header">
          <h3 id="plan-validation-h">{t("plan.validationTitle")}</h3>
        </div>
        {dirty && <p className="plan-stale-note">{t("plan.staleNote")}</p>}
        {!dirty &&
          SEVERITIES.map((sev) => {
            const group = issues.filter((i) => i.severity === sev);
            if (group.length === 0) return null;
            return (
              <div key={sev} className={`plan-issue-group ${sev}`}>
                <h4>
                  {t(`plan.sev.${sev}`)} ({group.length})
                </h4>
                <ul>
                  {group.map((i, k) => (
                    <li key={k}>
                      {i.route_id ? (
                        <button className="plan-issue-link" onClick={() => focusIssue(i)}>
                          {issueText(i, t, workingDoc)}
                        </button>
                      ) : (
                        <span>{issueText(i, t, workingDoc)}</span>
                      )}
                      {i.code === "order_on_another_plan" && onOpenPlan && (
                        <>
                          {" "}
                          <button
                            className="btn btn-ghost plan-icon-btn"
                            onClick={() => leave(() => onOpenPlan(String(i.params.plan_id)))}
                          >
                            {t("plan.openOtherPlan")}
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        {!dirty && validation?.status === "ok" && counts.blocking + counts.warning === 0 && (
          <p className="plan-ok">{t("plan.noIssues")}</p>
        )}
      </section>

      {workingDoc.trucks.length === 0 && unroutedSection}

      <section className="section-block plan-orders" aria-labelledby="plan-table-h">
        <div className="section-header plan-table-header">
          <h3 id="plan-table-h">{t("plan.tableLabel")}</h3>
          {editable && (
            <div className="plan-table-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setAddError(null);
                  setAdding({ routeId: null });
                }}
              >
                {t("plan.addOrders")}
              </button>
              <button className="btn btn-ghost" aria-label={t("plan.addTruck")} onClick={addTruck}>
                + {t("plan.addTruck")}
              </button>
            </div>
          )}
        </div>
        <div className="plan-table-wrap">
          <table className="plan-table" aria-labelledby="plan-table-h">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c} scope="col" className={`col-${c}`}>
                    {t(`plan.col.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            {workingDoc.trucks.map((tr) => [
              renderTruck(tr),
              ...routes.filter((r) => r.truck_id === tr.truck_id).map((r) => renderRoute(r)),
            ])}
          </table>
        </div>
        {workingDoc.trucks.length === 0 && (
          <p className="plan-muted">{editable ? t("plan.noTrucksYet") : t("plan.noTrucks")}</p>
        )}
      </section>

      {workingDoc.trucks.length > 0 && unroutedSection}

      {plan.document.trucks.some((tr) => tr.carrier === "3pl") && (
        <CarrierBookings
          planId={plan.id}
          planVersion={plan.version}
          dirty={dirty}
          onTrucksBooked={setBookedTrucks}
        />
      )}

      {plan.direct_3pl.length > 0 && (
        <section className="section-block plan-direct" aria-label={t("plan.direct3pl")}>
          <div className="section-header">
            <h3>{t("plan.direct3pl")}</h3>
          </div>
          <p className="plan-muted">{t("plan.direct3plNote")}</p>
          <table className="manifest">
            <thead>
              <tr>
                <th>{t("plan.pallet")}</th>
                <th>{t("plan.customer")}</th>
                <th>ZIP</th>
                <th>{t("plan.dims")}</th>
                <th>{t("plan.weight")}</th>
              </tr>
            </thead>
            <tbody>
              {plan.direct_3pl.map((p) => (
                <tr key={p.shipment_id}>
                  <td>{p.shipment_id}</td>
                  <td>{customerName(p.order_no)}</td>
                  <td>{p.dest_zip}</td>
                  <td>
                    {p.length_ft.toFixed(1)}×{p.width_ft.toFixed(1)}×{p.height_ft.toFixed(1)} ft
                  </td>
                  <td>{Math.round(p.weight_kg).toLocaleString()} kg</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {dialogOpen && (
        <PublishDialog
          t={t}
          plan={plan}
          mode={publishMode}
          busy={request === "publishing"}
          vehicleName={vehicleName}
          driverName={driverName}
          onConfirm={(ack) => void publish(ack)}
          onCancel={() => {
            setDialogOpen(false);
            pendingFocus.current = "plan-publish-btn";
          }}
        />
      )}
      {withdrawOpen && (
        <WithdrawDialog
          t={t}
          plan={plan}
          busy={request !== "idle"}
          vehicleName={vehicleName}
          driverName={driverName}
          onConfirm={() => void withdraw()}
          onCancel={() => {
            setWithdrawOpen(false);
            pendingFocus.current = "plan-withdraw-btn";
          }}
        />
      )}
      {adding && (
        <AddOrdersDialog
          t={t}
          region={plan.region}
          routes={openRoutes}
          routeLabel={labelOf}
          initialRouteId={adding.routeId}
          onPlan={onPlan}
          busy={request === "validating"}
          error={addError}
          onConfirm={(added) => void addOrders(added)}
          onCancel={() => setAdding(null)}
        />
      )}

      <div className="sr-only" aria-live="polite">
        {announcement}
      </div>
    </main>
  );
}

// A first publish, a retry of routes that failed to go out, or the edits
// made since drivers got the plan.
type PublishMode = "publish" | "retry" | "changes";

// The plan's routes with these ids, in plan order, then any the plan no
// longer has (a sent route a reset took away), as their driver last got them.
function routesById(plan: DispatchPlan, ids: string[]): PlanRoute[] {
  const inPlan = plan.document.routes.filter((r) => ids.includes(r.route_id));
  const gone = ids
    .filter((id) => !inPlan.some((r) => r.route_id === id))
    .map((id): PlanRoute => {
      const pub = plan.publication?.routes[id];
      const sent = pub && pub.status !== "not_applicable" ? pub.sent_as : null;
      return {
        route_id: id,
        carrier: "fleet",
        truck_id: null,
        vehicle_id: sent?.vehicle_id ?? null,
        driver_id: sent?.driver_id ?? null,
        sequence_locked: false,
        orders: [],
      };
    });
  return [...inPlan, ...gone];
}

function PublishDialog({
  t,
  plan,
  mode,
  busy,
  vehicleName,
  driverName,
  onConfirm,
  onCancel,
}: {
  t: T;
  plan: DispatchPlan;
  mode: PublishMode;
  busy: boolean;
  vehicleName: (id: string | null) => string;
  driverName: (id: string | null) => string;
  onConfirm: (acknowledge: boolean) => void;
  onCancel: () => void;
}) {
  const [ack, setAck] = useState(false);
  const warnings = issuesHoldingBack(plan, "warning");
  const { create, update, withdraw } = plan.changes;
  const lanes = plan.document.routes.filter((r) => r.carrier === "3pl" && r.orders.length > 0);
  const n = create.length + update.length + withdraw.length;
  // The driver a sent route went to, when the plan now names another.
  const sentDriver = (routeId: string) => {
    const pub = plan.publication?.routes[routeId];
    return pub && pub.status !== "not_applicable" ? (pub.sent_as?.driver_id ?? null) : null;
  };
  const driverCell = (r: PlanRoute) => {
    const before = sentDriver(r.route_id);
    return before && before !== r.driver_id
      ? `${driverName(before)} → ${driverName(r.driver_id)}`
      : driverName(r.driver_id);
  };

  const routeTable = (id: string, heading: string | null, routes: PlanRoute[]) => (
    <div className="plan-dialog-table" key={id}>
      {heading && <h4 id={id}>{heading}</h4>}
      <table className="manifest" aria-labelledby={heading ? id : undefined}>
        <thead>
          <tr>
            <th>{t("plan.route")}</th>
            <th>{t("plan.vehicle")}</th>
            <th>{t("plan.driver")}</th>
            <th>{t("plan.stops")}</th>
            <th>{t("plan.departs")}</th>
          </tr>
        </thead>
        <tbody>
          {routes.map((r) => (
            <tr key={r.route_id}>
              <td>{routeLabel(plan.document, r.route_id, t)}</td>
              <td>{vehicleName(r.vehicle_id)}</td>
              <td>{driverCell(r)}</td>
              <td>{routeStops(r).length}</td>
              <td>{formatClock(plan.validation?.routes[r.route_id]?.depart_min)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Dialog
      title={
        mode === "changes"
          ? t("plan.changesTitle", { n })
          : mode === "retry"
            ? t("plan.retryTitle", { n })
            : t("plan.publishTitle", { n })
      }
      busy={busy}
      onCancel={onCancel}
    >
      <p id="plan-dialog-desc">
        {mode === "changes"
          ? t("plan.changesDesc")
          : t("plan.publishDesc", { date: plan.plan_date })}
      </p>
      {lanes.length > 0 && (
        <p className="plan-muted">
          {t("plan.handedTo3pl", {
            routes: lanes.map((r) => routeLabel(plan.document, r.route_id, t)).join(", "),
          })}
        </p>
      )}
      {mode === "changes" ? (
        <>
          {create.length > 0 &&
            routeTable("plan-changes-new", t("plan.changesNew"), routesById(plan, create))}
          {update.length > 0 &&
            routeTable("plan-changes-updated", t("plan.changesUpdated"), routesById(plan, update))}
          {withdraw.length > 0 && (
            <div className="plan-dialog-table">
              <h4 id="plan-changes-removed">{t("plan.changesRemoved")}</h4>
              <table className="manifest" aria-labelledby="plan-changes-removed">
                <thead>
                  <tr>
                    <th>{t("plan.route")}</th>
                    <th>{t("plan.driver")}</th>
                  </tr>
                </thead>
                <tbody>
                  {routesById(plan, withdraw).map((r) => (
                    <tr key={r.route_id}>
                      <td>{routeLabel(plan.document, r.route_id, t)}</td>
                      <td>{driverName(sentDriver(r.route_id) ?? r.driver_id)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        routeTable("plan-changes-new", null, routesById(plan, create))
      )}
      {warnings.length > 0 && (
        <div className="plan-dialog-warnings">
          <h4>{t("plan.warningsToAck", { n: warnings.length })}</h4>
          <ul>
            {warnings.map((w, k) => (
              <li key={k}>{issueText(w, t, plan.document)}</li>
            ))}
          </ul>
          <label>
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              disabled={busy}
            />{" "}
            {t("plan.ackWarnings")}
          </label>
        </div>
      )}
      <p className="plan-muted">
        {t(plan.publisher_simulated ? "plan.publishNote" : "plan.publishNoteLive")}
      </p>
      <div className="plan-dialog-actions">
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          {t("plan.cancel")}
        </button>
        <button
          className="btn btn-primary"
          onClick={() => onConfirm(ack)}
          disabled={busy || (warnings.length > 0 && !ack)}
        >
          {busy
            ? t("plan.publishing")
            : mode === "retry"
              ? t("plan.confirmRetry")
              : mode === "changes"
                ? t("plan.publishChanges")
                : t("plan.confirmPublish")}
        </button>
      </div>
    </Dialog>
  );
}

function WithdrawDialog({
  t,
  plan,
  busy,
  vehicleName,
  driverName,
  onConfirm,
  onCancel,
}: {
  t: T;
  plan: DispatchPlan;
  busy: boolean;
  vehicleName: (id: string | null) => string;
  driverName: (id: string | null) => string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const pub = plan.publication?.routes ?? {};
  const routes = routesById(
    plan,
    Object.keys(pub).filter((rid) => pub[rid].status === "sent"),
  );
  return (
    <Dialog
      title={t("plan.withdrawTitle", { n: routes.length })}
      busy={busy}
      onCancel={onCancel}
    >
      <p id="plan-dialog-desc">{t("plan.withdrawDesc")}</p>
      <div className="plan-dialog-table">
        <table className="manifest">
          <thead>
            <tr>
              <th>{t("plan.route")}</th>
              <th>{t("plan.vehicle")}</th>
              <th>{t("plan.driver")}</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((r) => {
              // What the driver has, which may differ from unsent edits.
              const sent = pub[r.route_id];
              const had = sent?.status === "sent" ? sent.sent_as : null;
              return (
                <tr key={r.route_id}>
                  <td>{routeLabel(plan.document, r.route_id, t)}</td>
                  <td>{vehicleName(had?.vehicle_id ?? r.vehicle_id)}</td>
                  <td>{driverName(had?.driver_id ?? r.driver_id)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="plan-muted">
        {t(plan.publisher_simulated ? "plan.withdrawNote" : "plan.withdrawNoteLive")}
      </p>
      <div className="plan-dialog-actions">
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          {t("plan.cancel")}
        </button>
        <button className="btn btn-danger" onClick={onConfirm} disabled={busy}>
          {busy ? t("plan.withdrawing") : t("plan.confirmWithdraw")}
        </button>
      </div>
    </Dialog>
  );
}
